import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, Layers, Map as MapIcon } from 'lucide-react';
import {
  Map,
  MapFitBounds,
  MapInvalidateSize,
  MapMarker,
  MapPolyline,
  MapTileLayer,
  MapZoomControl,
  createLabeledMapIcon,
} from '../UI/map';
import { Card, CardContent } from '../UI/card';
import { Badge } from '../UI/badge';
import { Button } from '../UI/button';
import { EmptyState } from '../UI/empty-state';
import { Skeleton } from '../UI/skeleton';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '../UI/dropdown-menu';
import WatchlistMapDetailPanel from './WatchlistMapDetailPanel';
import { useLocale } from '../../contexts/LocaleContext';
import { colors } from '../../config/colors';
import { themeClasses } from '../../config/themeClasses';
import { markerColorFromRisk, routeColorFromRisk } from '../../utils/watchlistMapModel';
import { getMarkerBreadcrumb } from '../../utils/tenantMapModel';
import { cn } from '../../lib/utils';

const DEFAULT_CENTER = [20, 0];
const HIGH_RISK_THRESHOLD = 7;

const MARKER_TYPE_COLORS = {
  own: colors.secondary[500],
  supplier: colors.danger[500],
  company: colors.brand[500],
};

const isFacilityMarker = (marker) =>
  marker?.type === 'company' || marker?.type === 'own' || marker?.type === 'supplier';

function WatchlistSupplyChainMap({
  markers = [],
  routes = [],
  bounds = [],
  loading = false,
  geocoding = false,
  selectedMarkerId,
  selectedRouteId: controlledRouteId,
  onMarkerSelect,
  onRouteSelect,
  onViewRouteAnalysis,
  onAddCompany,
}) {
  const { t } = useLocale();
  const [layers, setLayers] = useState({
    companies: true,
    logistics: true,
    network: true,
    highRiskOnly: false,
  });
  const [internalRouteId, setInternalRouteId] = useState(null);

  const selectedRouteId = controlledRouteId ?? internalRouteId;

  const selectedMarker = useMemo(
    () => markers.find((marker) => marker.id === selectedMarkerId) || null,
    [markers, selectedMarkerId]
  );

  const selectedRoute = useMemo(
    () => routes.find((route) => route.id === selectedRouteId) || null,
    [routes, selectedRouteId]
  );

  const visibleMarkers = useMemo(() => {
    let filtered = markers.filter((marker) => {
      if (!layers.companies) return false;
      return isFacilityMarker(marker);
    });
    if (layers.highRiskOnly) {
      filtered = filtered.filter(
        (marker) =>
          Number(marker.riskScore) >= HIGH_RISK_THRESHOLD ||
          String(marker.priority || '').toLowerCase() === 'high'
      );
    }
    return filtered;
  }, [markers, layers]);

  const visibleRoutes = useMemo(
    () =>
      routes.filter((route) => {
        if (route.type === 'logistics') return layers.logistics;
        if (route.type === 'network') return layers.network;
        return true;
      }),
    [routes, layers]
  );

  const highlightedRouteIds = useMemo(() => {
    if (!selectedMarker) return new Set();
    const companyIds = new Set(
      selectedMarker.companyIds?.length
        ? selectedMarker.companyIds
        : [
            selectedMarker.companyId,
            selectedMarker.supplierTicker,
          ].filter(Boolean)
    );
    return new Set(
      visibleRoutes
        .filter((route) => {
          if (
            route.fromMarkerId === selectedMarker.id ||
            route.toMarkerId === selectedMarker.id
          ) {
            return true;
          }
          if (route.type === 'network') {
            return (
              companyIds.has(route.fromCompanyId) || companyIds.has(route.toCompanyId)
            );
          }
          return route.fromCompanyId && companyIds.has(route.fromCompanyId);
        })
        .map((route) => route.id)
    );
  }, [selectedMarker, visibleRoutes]);

  const fitBounds = useMemo(() => {
    const points = [];
    visibleMarkers.forEach((marker) => points.push(marker.position));
    visibleRoutes.forEach((route) => (route.positions || []).forEach((point) => points.push(point)));
    return points.length > 0 ? points : bounds;
  }, [visibleMarkers, visibleRoutes, bounds]);

  const breadcrumb = useMemo(
    () => getMarkerBreadcrumb(selectedMarker, selectedMarker?.title || ''),
    [selectedMarker]
  );

  useEffect(() => {
    if (selectedMarkerId || markers.length === 0) return;
    const firstOwn = markers.find((marker) => marker.type === 'own');
    const fallback = firstOwn || markers.find(isFacilityMarker);
    if (fallback) onMarkerSelect?.(fallback.id);
  }, [markers, selectedMarkerId, onMarkerSelect]);

  const toggleLayer = (key) => {
    setLayers((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleMarkerClick = (markerId) => {
    setInternalRouteId(null);
    onRouteSelect?.(null);
    onMarkerSelect?.(markerId);
  };

  const handleRouteClick = (routeId) => {
    setInternalRouteId(routeId);
    onRouteSelect?.(routeId);
    onMarkerSelect?.(null);
  };

  const handleClosePanel = () => {
    setInternalRouteId(null);
    onRouteSelect?.(null);
    onMarkerSelect?.(null);
  };

  const showDataOverlay = loading && markers.length === 0 && routes.length === 0;

  if (!loading && !geocoding && markers.length === 0 && routes.length === 0) {
    return (
      <EmptyState
        icon={MapIcon}
        title={t('watchlistMapEmptyTitle')}
        description={t('watchlistMapEmptyDescription')}
        actionLabel={t('watchlistMapEmptyCta')}
        onAction={onAddCompany}
        className="min-h-[520px]"
      />
    );
  }

  return (
    <Card className={cn(themeClasses.appKit.sectionShell, 'overflow-hidden')}>
      <CardContent className="p-0">
        <div className="relative h-[min(72vh,640px)] min-h-[520px] w-full">
          {showDataOverlay ? (
            <div className="absolute inset-0 z-30 flex items-center justify-center bg-background-card/80">
              <div className="flex flex-col items-center gap-2">
                <Skeleton className="h-8 w-48" />
                <p className="text-sm text-text-secondary">{t('watchlistMapLoading')}</p>
              </div>
            </div>
          ) : null}

          {geocoding ? (
            <div className="absolute right-4 top-4 z-20">
              <Badge variant="secondary">{t('watchlistMapGeocoding')}</Badge>
            </div>
          ) : null}

          {selectedMarker ? (
            <div className="absolute left-4 top-4 z-20 max-w-[min(70%,320px)]">
              <div className="rounded-lg border border-border-light bg-background-card/90 px-3 py-2 text-xs text-text-secondary shadow-md backdrop-blur-sm">
                <span className="text-text-primary">{breadcrumb.location}</span>
                {breadcrumb.name ? (
                  <>
                    <span className="mx-1.5 text-text-tertiary">{t('watchlistMapBreadcrumbSeparator')}</span>
                    <span className="font-medium text-text-primary">{breadcrumb.name}</span>
                  </>
                ) : null}
              </div>
            </div>
          ) : null}

          <div className="absolute bottom-4 left-4 z-20">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" variant="secondary" size="sm" className="gap-2 shadow-md">
                  <Layers className="h-4 w-4" />
                  {t('watchlistMapLayersTitle')}
                  <ChevronDown className="h-3.5 w-3.5 opacity-70" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                {[
                  { key: 'companies', labelKey: 'watchlistMapLayerCompanies' },
                  { key: 'logistics', labelKey: 'watchlistMapLayerLogistics' },
                  { key: 'network', labelKey: 'watchlistMapLayerNetwork' },
                  { key: 'highRiskOnly', labelKey: 'watchlistMapLayerHighRiskOnly' },
                ].map((layer) => (
                  <DropdownMenuCheckboxItem
                    key={layer.key}
                    checked={layers[layer.key]}
                    onCheckedChange={() => toggleLayer(layer.key)}
                  >
                    {t(layer.labelKey)}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {selectedRoute?.type === 'network' ? (
            <div className="pointer-events-none absolute left-1/2 top-1/2 z-20 max-w-[280px] -translate-x-1/2 -translate-y-1/2">
              <div className="rounded-lg border border-secondary-500/40 bg-background-card/95 px-3 py-2 text-center text-xs text-text-primary shadow-lg backdrop-blur-sm">
                {t('watchlistMapRouteRelationshipTooltip', {
                  company: selectedRoute.subtitle || selectedRoute.title || '',
                })}
              </div>
            </div>
          ) : null}

          <Map center={DEFAULT_CENTER} zoom={2} className="h-full w-full rounded-none border-0">
            <MapTileLayer variant="dark" />
            <MapZoomControl />
            <MapInvalidateSize
              invalidateKey={`${markers.length}-${routes.length}-${geocoding}-${selectedMarkerId}-${selectedRouteId}`}
            />
            {fitBounds.length > 0 ? <MapFitBounds bounds={fitBounds} padding={[72, 72]} /> : null}

            {visibleRoutes.map((route) => {
              const isHighlighted =
                selectedRouteId === route.id || highlightedRouteIds.has(route.id);
              const isNetwork = route.type === 'network';
              const lineColor = route.color || routeColorFromRisk(route.riskScore);
              return (
                <MapPolyline
                  key={route.id}
                  positions={route.positions}
                  color={isNetwork ? colors.secondary[500] : lineColor}
                  opacity={isHighlighted ? 0.95 : isNetwork ? 0.55 : 0.45}
                  weight={isHighlighted ? 3.5 : 2}
                  dashed={isNetwork}
                  eventHandlers={{
                    click: () => handleRouteClick(route.id),
                  }}
                />
              );
            })}

            {visibleMarkers.map((marker) => {
              const isSelected = selectedMarkerId === marker.id;
              const markerColor = isSelected
                  ? colors.secondary[500]
                  : marker.riskScore != null && Number(marker.riskScore) > 0
                    ? markerColorFromRisk(marker.riskScore, marker.priority)
                    : marker.iconColor ||
                      MARKER_TYPE_COLORS[marker.type] ||
                      markerColorFromRisk(marker.riskScore, marker.priority);
              const icon = createLabeledMapIcon({
                color: markerColor,
                label: marker.title,
                selected: isSelected,
                variant: 'default',
              });

              return (
                <MapMarker
                  key={marker.id}
                  position={marker.position}
                  icon={icon}
                  eventHandlers={{
                    click: () => handleMarkerClick(marker.id),
                  }}
                />
              );
            })}
          </Map>

          {selectedMarker || selectedRoute ? (
            <WatchlistMapDetailPanel
              marker={selectedMarker}
              route={selectedRoute}
              markers={markers}
              onClose={handleClosePanel}
              onMarkerSelect={handleMarkerClick}
              onViewRouteAnalysis={onViewRouteAnalysis}
            />
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

export default WatchlistSupplyChainMap;
